/*!
 * js-speck v0.1.0
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */

// src/bytes.ts
var HAS_TYPED_ARRAY = typeof ArrayBuffer !== "undefined" && typeof Uint8Array !== "undefined" && typeof Uint32Array !== "undefined";
var ByteArrayClass = HAS_TYPED_ARRAY ? Uint8Array : Array;
var WordArrayClass = HAS_TYPED_ARRAY ? Uint32Array : Array;
var INPUT_ERROR = "input is invalid type";
var isArray = !Array.isArray ? function(obj) {
  return Object.prototype.toString.call(obj) === "[object Array]";
} : Array.isArray;
function toByteArray(input) {
  const type = typeof input;
  if (type !== "object" || input === null) {
    throw new Error(INPUT_ERROR);
  }
  if (HAS_TYPED_ARRAY) {
    if (input instanceof Uint8Array) {
      return input;
    }
    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input);
    }
  }
  if (!isArray(input)) {
    throw new Error(INPUT_ERROR);
  }
  return input;
}
function assertByteLength(input, length, name) {
  if (input.length !== length) {
    throw new RangeError(`${name} must be ${length} bytes.`);
  }
}
function toWordLE(bytes, wordBytes) {
  var words = new WordArrayClass(bytes.length / wordBytes);
  for (var i = 0; i < words.length; ++i) {
    var offset = i * wordBytes;
    var value = 0;
    for (var j = 0; j < wordBytes; ++j) {
      value += bytes[offset + j] * Math.pow(2, j * 8);
    }
    words[i] = value >>> 0;
  }
  return words;
}
function fromWordLE(words, wordBytes) {
  var bytes = new ByteArrayClass(words.length * wordBytes);
  for (var i = 0; i < words.length; ++i) {
    var offset = i * wordBytes;
    var value = words[i];
    for (var j = 0; j < wordBytes; ++j) {
      bytes[offset + j] = Math.floor(value / Math.pow(2, j * 8)) & 255;
    }
  }
  return bytes;
}

// src/speck.ts
function createMask(wordBits) {
  return wordBits === 32 ? 4294967295 : (1 << wordBits) - 1;
}
function normalize(value, mask, wordBits) {
  return wordBits === 32 ? value >>> 0 : value & mask;
}
function rotr(value, shift, wordBits, mask) {
  return normalize(value >>> shift | value << wordBits - shift, mask, wordBits);
}
function rotl(value, shift, wordBits, mask) {
  return normalize(value << shift | value >>> wordBits - shift, mask, wordBits);
}
function createSpeckVariant(config) {
  const wordBytes = config.wordBits / 8;
  const blockBytes = wordBytes * 2;
  const keyByteLength = wordBytes * config.keyWords;
  const mask = createMask(config.wordBits);
  function round(x, y, key) {
    x = normalize(rotr(x, config.alpha, config.wordBits, mask) + y, mask, config.wordBits);
    x = normalize(x ^ key, mask, config.wordBits);
    y = normalize(rotl(y, config.beta, config.wordBits, mask) ^ x, mask, config.wordBits);
    return [x, y];
  }
  function inverseRound(x, y, key) {
    y = rotr(normalize(y ^ x, mask, config.wordBits), config.beta, config.wordBits, mask);
    x = rotl(normalize((x ^ key) - y, mask, config.wordBits), config.alpha, config.wordBits, mask);
    return [x, y];
  }
  function assertBlockMultiple(bytes, name) {
    if (bytes.length % blockBytes !== 0) {
      throw new RangeError(`${name} length must be a multiple of ${blockBytes} bytes.`);
    }
  }
  function assertWordLength(words, length, name) {
    if (words.length !== length) {
      throw new RangeError(`${name} must contain ${length} words.`);
    }
  }
  function assertWordBlockMultiple(words, name) {
    if (words.length % 2 !== 0) {
      throw new RangeError(`${name} length must be a multiple of 2 words.`);
    }
  }
  function assertRoundKeysAndDataWords(roundKeys, dataWords) {
    if (roundKeys.length !== config.rounds) {
      throw new RangeError(`Round keys must contain ${config.rounds} words.`);
    }
    assertWordBlockMultiple(dataWords, "Data");
  }
  function toWords(key, data) {
    const keyBytes = toByteArray(key);
    assertByteLength(keyBytes, keyByteLength, "Key");
    const dataBytes = toByteArray(data);
    assertBlockMultiple(dataBytes, "Data");
    return [toWordLE(keyBytes, wordBytes), toWordLE(dataBytes, wordBytes)];
  }
  function expandKey11(keyWords) {
    assertWordLength(keyWords, config.keyWords, "Key");
    const l = new WordArrayClass(config.rounds + config.keyWords - 2);
    const roundKeys = new WordArrayClass(config.rounds);
    roundKeys[0] = keyWords[0];
    for (let i = 1; i < config.keyWords; ++i) {
      l[i - 1] = keyWords[i];
    }
    for (let i = 0; i < config.rounds - 1; ++i) {
      const result = round(l[i], roundKeys[i], i);
      l[i + config.keyWords - 1] = result[0];
      roundKeys[i + 1] = result[1];
    }
    return roundKeys;
  }
  function encrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromWordLE(encryptWords11(keyWords, dataWords), wordBytes);
  }
  function encryptWords11(keyWords, dataWords) {
    return encryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function encryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 2) {
      let y = dataWords[i];
      let x = dataWords[i + 1];
      for (let j = 0; j < config.rounds; ++j) {
        const r = round(x, y, roundKeys[j]);
        x = r[0];
        y = r[1];
      }
      result[i] = y;
      result[i + 1] = x;
    }
    return result;
  }
  function decrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromWordLE(decryptWords11(keyWords, dataWords), wordBytes);
  }
  function decryptWords11(keyWords, dataWords) {
    return decryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function decryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 2) {
      let y = dataWords[i];
      let x = dataWords[i + 1];
      for (let j = config.rounds - 1; j >= 0; --j) {
        const r = inverseRound(x, y, roundKeys[j]);
        x = r[0];
        y = r[1];
      }
      result[i] = y;
      result[i + 1] = x;
    }
    return result;
  }
  return {
    blockBytes,
    keyBytes: keyByteLength,
    rounds: config.rounds,
    expandKey: expandKey11,
    encrypt: encrypt11,
    decrypt: decrypt11,
    encryptWords: encryptWords11,
    decryptWords: decryptWords11,
    encryptWordsWithRoundKey: encryptWordsWithRoundKey11,
    decryptWordsWithRoundKey: decryptWordsWithRoundKey11
  };
}

// src/speck32-64.ts
var speck32_64Block = createSpeckVariant({
  name: "Speck32/64",
  wordBits: 16,
  keyWords: 4,
  rounds: 22,
  alpha: 7,
  beta: 2
});
var {
  expandKey,
  encrypt,
  decrypt,
  encryptWords,
  decryptWords,
  encryptWordsWithRoundKey,
  decryptWordsWithRoundKey
} = speck32_64Block;

// src/speck48-72.ts
var speck48_72Block = createSpeckVariant({
  name: "Speck48/72",
  wordBits: 24,
  keyWords: 3,
  rounds: 22,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey2,
  encrypt: encrypt2,
  decrypt: decrypt2,
  encryptWords: encryptWords2,
  decryptWords: decryptWords2,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey2,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey2
} = speck48_72Block;

// src/speck48-96.ts
var speck48_96Block = createSpeckVariant({
  name: "Speck48/96",
  wordBits: 24,
  keyWords: 4,
  rounds: 23,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey3,
  encrypt: encrypt3,
  decrypt: decrypt3,
  encryptWords: encryptWords3,
  decryptWords: decryptWords3,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey3,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey3
} = speck48_96Block;

// src/speck64-96.ts
var speck64_96Block = createSpeckVariant({
  name: "Speck64/96",
  wordBits: 32,
  keyWords: 3,
  rounds: 26,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey4,
  encrypt: encrypt4,
  decrypt: decrypt4,
  encryptWords: encryptWords4,
  decryptWords: decryptWords4,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey4,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey4
} = speck64_96Block;

// src/speck64-128.ts
var speck64_128Block = createSpeckVariant({
  name: "Speck64/128",
  wordBits: 32,
  keyWords: 4,
  rounds: 27,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey5,
  encrypt: encrypt5,
  decrypt: decrypt5,
  encryptWords: encryptWords5,
  decryptWords: decryptWords5,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey5,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey5
} = speck64_128Block;

// src/speck-long.ts
function addLong(aLo, aHi, bLo, bHi, highMask) {
  const lo = aLo + bLo >>> 0;
  const hi = (aHi + bHi + (lo < aLo >>> 0 ? 1 : 0) & highMask) >>> 0;
  return [lo, hi];
}
function subLong(aLo, aHi, bLo, bHi, highMask) {
  const lo = aLo - bLo >>> 0;
  const hi = (aHi - bHi - (aLo >>> 0 < bLo >>> 0 ? 1 : 0) & highMask) >>> 0;
  return [lo, hi];
}
function rotlLong(lo, hi, shift, highBits, highMask) {
  return [
    (lo << shift | hi >>> highBits - shift) >>> 0,
    ((hi << shift | lo >>> 32 - shift) & highMask) >>> 0
  ];
}
function rotrLong(lo, hi, shift, highBits, highMask) {
  const shiftMask = (1 << shift) - 1;
  return [
    (lo >>> shift | (hi & shiftMask) << 32 - shift) >>> 0,
    ((hi >>> shift | (lo & shiftMask) << highBits - shift) & highMask) >>> 0
  ];
}
function toLongWordPairsLE(bytes, wordBytes) {
  const lanesPerWord = 2;
  const words = new WordArrayClass(bytes.length / wordBytes * lanesPerWord);
  for (let i = 0; i < words.length; i += lanesPerWord) {
    const offset = i / lanesPerWord * wordBytes;
    words[i] = (bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16 | bytes[offset + 3] << 24) >>> 0;
    let high = 0;
    for (let j = 4; j < wordBytes; ++j) {
      high |= bytes[offset + j] << (j - 4) * 8;
    }
    words[i + 1] = high >>> 0;
  }
  return words;
}
function fromLongWordPairsLE(words, wordBytes) {
  const lanesPerWord = 2;
  const bytes = new ByteArrayClass(words.length / lanesPerWord * wordBytes);
  for (let i = 0; i < words.length; i += lanesPerWord) {
    const lo = words[i];
    const hi = words[i + 1];
    const offset = i / lanesPerWord * wordBytes;
    bytes[offset] = lo & 255;
    bytes[offset + 1] = lo >>> 8 & 255;
    bytes[offset + 2] = lo >>> 16 & 255;
    bytes[offset + 3] = lo >>> 24 & 255;
    for (let j = 4; j < wordBytes; ++j) {
      bytes[offset + j] = hi >>> (j - 4) * 8 & 255;
    }
  }
  return bytes;
}
function createSpeckLongVariant(config) {
  const lanesPerWord = 2;
  const wordBytes = config.wordBits / 8;
  const highBits = config.wordBits - 32;
  const highMask = config.wordBits === 64 ? 4294967295 : (1 << highBits) - 1;
  const blockBytes = wordBytes * 2;
  const keyByteLength = wordBytes * config.keyWords;
  const keyLaneLength = config.keyWords * lanesPerWord;
  const roundKeyLaneLength = config.rounds * lanesPerWord;
  function round(xLo, xHi, yLo, yHi, keyLo, keyHi) {
    let rotated = rotrLong(xLo, xHi, config.alpha, highBits, highMask);
    rotated = addLong(rotated[0], rotated[1], yLo, yHi, highMask);
    xLo = (rotated[0] ^ keyLo) >>> 0;
    xHi = ((rotated[1] ^ keyHi) & highMask) >>> 0;
    rotated = rotlLong(yLo, yHi, config.beta, highBits, highMask);
    yLo = (rotated[0] ^ xLo) >>> 0;
    yHi = ((rotated[1] ^ xHi) & highMask) >>> 0;
    return [xLo, xHi, yLo, yHi];
  }
  function inverseRound(xLo, xHi, yLo, yHi, keyLo, keyHi) {
    let rotated = rotrLong((yLo ^ xLo) >>> 0, ((yHi ^ xHi) & highMask) >>> 0, config.beta, highBits, highMask);
    yLo = rotated[0];
    yHi = rotated[1];
    const subtracted = subLong((xLo ^ keyLo) >>> 0, ((xHi ^ keyHi) & highMask) >>> 0, yLo, yHi, highMask);
    rotated = rotlLong(subtracted[0], subtracted[1], config.alpha, highBits, highMask);
    xLo = rotated[0];
    xHi = rotated[1];
    return [xLo, xHi, yLo, yHi];
  }
  function assertBlockMultiple(bytes, name) {
    if (bytes.length % blockBytes !== 0) {
      throw new RangeError(`${name} length must be a multiple of ${blockBytes} bytes.`);
    }
  }
  function assertLaneLength(words, length, name) {
    if (words.length !== length) {
      throw new RangeError(`${name} must contain ${length} 32-bit lanes.`);
    }
  }
  function assertLaneBlockMultiple(words, name) {
    if (words.length % 4 !== 0) {
      throw new RangeError(`${name} length must be a multiple of 4 32-bit lanes.`);
    }
  }
  function assertRoundKeysAndDataWords(roundKeys, dataWords) {
    if (roundKeys.length !== roundKeyLaneLength) {
      throw new RangeError(`Round keys must contain ${roundKeyLaneLength} 32-bit lanes.`);
    }
    assertLaneBlockMultiple(dataWords, "Data");
  }
  function toWords(key, data) {
    const keyBytes = toByteArray(key);
    assertByteLength(keyBytes, keyByteLength, "Key");
    const dataBytes = toByteArray(data);
    assertBlockMultiple(dataBytes, "Data");
    return [toLongWordPairsLE(keyBytes, wordBytes), toLongWordPairsLE(dataBytes, wordBytes)];
  }
  function expandKey11(keyWords) {
    assertLaneLength(keyWords, keyLaneLength, "Key");
    const l = new WordArrayClass((config.rounds + config.keyWords - 2) * lanesPerWord);
    const roundKeys = new WordArrayClass(roundKeyLaneLength);
    roundKeys[0] = keyWords[0];
    roundKeys[1] = keyWords[1];
    for (let i = 1; i < config.keyWords; ++i) {
      const target = (i - 1) * lanesPerWord;
      const source = i * lanesPerWord;
      l[target] = keyWords[source];
      l[target + 1] = keyWords[source + 1];
    }
    for (let i = 0; i < config.rounds - 1; ++i) {
      const lOffset = i * lanesPerWord;
      const keyOffset = i * lanesPerWord;
      const result = round(l[lOffset], l[lOffset + 1], roundKeys[keyOffset], roundKeys[keyOffset + 1], i, 0);
      const nextLOffset = (i + config.keyWords - 1) * lanesPerWord;
      const nextKeyOffset = (i + 1) * lanesPerWord;
      l[nextLOffset] = result[0];
      l[nextLOffset + 1] = result[1];
      roundKeys[nextKeyOffset] = result[2];
      roundKeys[nextKeyOffset + 1] = result[3];
    }
    return roundKeys;
  }
  function encrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromLongWordPairsLE(encryptWords11(keyWords, dataWords), wordBytes);
  }
  function encryptWords11(keyWords, dataWords) {
    return encryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function encryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 4) {
      let yLo = dataWords[i];
      let yHi = dataWords[i + 1];
      let xLo = dataWords[i + 2];
      let xHi = dataWords[i + 3];
      for (let j = 0; j < config.rounds; ++j) {
        const keyOffset = j * lanesPerWord;
        const r = round(xLo, xHi, yLo, yHi, roundKeys[keyOffset], roundKeys[keyOffset + 1]);
        xLo = r[0];
        xHi = r[1];
        yLo = r[2];
        yHi = r[3];
      }
      result[i] = yLo;
      result[i + 1] = yHi;
      result[i + 2] = xLo;
      result[i + 3] = xHi;
    }
    return result;
  }
  function decrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromLongWordPairsLE(decryptWords11(keyWords, dataWords), wordBytes);
  }
  function decryptWords11(keyWords, dataWords) {
    return decryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function decryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 4) {
      let yLo = dataWords[i];
      let yHi = dataWords[i + 1];
      let xLo = dataWords[i + 2];
      let xHi = dataWords[i + 3];
      for (let j = config.rounds - 1; j >= 0; --j) {
        const keyOffset = j * lanesPerWord;
        const r = inverseRound(xLo, xHi, yLo, yHi, roundKeys[keyOffset], roundKeys[keyOffset + 1]);
        xLo = r[0];
        xHi = r[1];
        yLo = r[2];
        yHi = r[3];
      }
      result[i] = yLo;
      result[i + 1] = yHi;
      result[i + 2] = xLo;
      result[i + 3] = xHi;
    }
    return result;
  }
  return {
    blockBytes,
    keyBytes: keyByteLength,
    rounds: config.rounds,
    expandKey: expandKey11,
    encrypt: encrypt11,
    decrypt: decrypt11,
    encryptWords: encryptWords11,
    decryptWords: decryptWords11,
    encryptWordsWithRoundKey: encryptWordsWithRoundKey11,
    decryptWordsWithRoundKey: decryptWordsWithRoundKey11
  };
}

// src/speck96-96.ts
var speck96_96Block = createSpeckLongVariant({
  wordBits: 48,
  keyWords: 2,
  rounds: 28,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey6,
  encrypt: encrypt6,
  decrypt: decrypt6,
  encryptWords: encryptWords6,
  decryptWords: decryptWords6,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey6,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey6
} = speck96_96Block;

// src/speck96-144.ts
var speck96_144Block = createSpeckLongVariant({
  wordBits: 48,
  keyWords: 3,
  rounds: 29,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey7,
  encrypt: encrypt7,
  decrypt: decrypt7,
  encryptWords: encryptWords7,
  decryptWords: decryptWords7,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey7,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey7
} = speck96_144Block;

// src/speck128-128.ts
var speck128_128Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 2,
  rounds: 32,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey8,
  encrypt: encrypt8,
  decrypt: decrypt8,
  encryptWords: encryptWords8,
  decryptWords: decryptWords8,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey8,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey8
} = speck128_128Block;

// src/speck128-192.ts
var speck128_192Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 3,
  rounds: 33,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey9,
  encrypt: encrypt9,
  decrypt: decrypt9,
  encryptWords: encryptWords9,
  decryptWords: decryptWords9,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey9,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey9
} = speck128_192Block;

// src/speck128-256.ts
var speck128_256Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 4,
  rounds: 34,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey10,
  encrypt: encrypt10,
  decrypt: decrypt10,
  encryptWords: encryptWords10,
  decryptWords: decryptWords10,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey10,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey10
} = speck128_256Block;

// src/index.ts
var speck32_64 = speck32_64Block;
var speck48_72 = speck48_72Block;
var speck48_96 = speck48_96Block;
var speck64_96 = speck64_96Block;
var speck64_128 = speck64_128Block;
var speck96_96 = speck96_96Block;
var speck96_144 = speck96_144Block;
var speck128_128 = speck128_128Block;
var speck128_192 = speck128_192Block;
var speck128_256 = speck128_256Block;
export {
  decrypt8 as decryptSpeck128_128,
  decryptWords8 as decryptSpeck128_128Words,
  decryptWordsWithRoundKey8 as decryptSpeck128_128WordsWithRoundKey,
  decrypt9 as decryptSpeck128_192,
  decryptWords9 as decryptSpeck128_192Words,
  decryptWordsWithRoundKey9 as decryptSpeck128_192WordsWithRoundKey,
  decrypt10 as decryptSpeck128_256,
  decryptWords10 as decryptSpeck128_256Words,
  decryptWordsWithRoundKey10 as decryptSpeck128_256WordsWithRoundKey,
  decrypt as decryptSpeck32_64,
  decryptWords as decryptSpeck32_64Words,
  decryptWordsWithRoundKey as decryptSpeck32_64WordsWithRoundKey,
  decrypt2 as decryptSpeck48_72,
  decryptWords2 as decryptSpeck48_72Words,
  decryptWordsWithRoundKey2 as decryptSpeck48_72WordsWithRoundKey,
  decrypt3 as decryptSpeck48_96,
  decryptWords3 as decryptSpeck48_96Words,
  decryptWordsWithRoundKey3 as decryptSpeck48_96WordsWithRoundKey,
  decrypt5 as decryptSpeck64_128,
  decryptWords5 as decryptSpeck64_128Words,
  decryptWordsWithRoundKey5 as decryptSpeck64_128WordsWithRoundKey,
  decrypt4 as decryptSpeck64_96,
  decryptWords4 as decryptSpeck64_96Words,
  decryptWordsWithRoundKey4 as decryptSpeck64_96WordsWithRoundKey,
  decrypt7 as decryptSpeck96_144,
  decryptWords7 as decryptSpeck96_144Words,
  decryptWordsWithRoundKey7 as decryptSpeck96_144WordsWithRoundKey,
  decrypt6 as decryptSpeck96_96,
  decryptWords6 as decryptSpeck96_96Words,
  decryptWordsWithRoundKey6 as decryptSpeck96_96WordsWithRoundKey,
  encrypt8 as encryptSpeck128_128,
  encryptWords8 as encryptSpeck128_128Words,
  encryptWordsWithRoundKey8 as encryptSpeck128_128WordsWithRoundKey,
  encrypt9 as encryptSpeck128_192,
  encryptWords9 as encryptSpeck128_192Words,
  encryptWordsWithRoundKey9 as encryptSpeck128_192WordsWithRoundKey,
  encrypt10 as encryptSpeck128_256,
  encryptWords10 as encryptSpeck128_256Words,
  encryptWordsWithRoundKey10 as encryptSpeck128_256WordsWithRoundKey,
  encrypt as encryptSpeck32_64,
  encryptWords as encryptSpeck32_64Words,
  encryptWordsWithRoundKey as encryptSpeck32_64WordsWithRoundKey,
  encrypt2 as encryptSpeck48_72,
  encryptWords2 as encryptSpeck48_72Words,
  encryptWordsWithRoundKey2 as encryptSpeck48_72WordsWithRoundKey,
  encrypt3 as encryptSpeck48_96,
  encryptWords3 as encryptSpeck48_96Words,
  encryptWordsWithRoundKey3 as encryptSpeck48_96WordsWithRoundKey,
  encrypt5 as encryptSpeck64_128,
  encryptWords5 as encryptSpeck64_128Words,
  encryptWordsWithRoundKey5 as encryptSpeck64_128WordsWithRoundKey,
  encrypt4 as encryptSpeck64_96,
  encryptWords4 as encryptSpeck64_96Words,
  encryptWordsWithRoundKey4 as encryptSpeck64_96WordsWithRoundKey,
  encrypt7 as encryptSpeck96_144,
  encryptWords7 as encryptSpeck96_144Words,
  encryptWordsWithRoundKey7 as encryptSpeck96_144WordsWithRoundKey,
  encrypt6 as encryptSpeck96_96,
  encryptWords6 as encryptSpeck96_96Words,
  encryptWordsWithRoundKey6 as encryptSpeck96_96WordsWithRoundKey,
  expandKey8 as expandSpeck128_128Key,
  expandKey9 as expandSpeck128_192Key,
  expandKey10 as expandSpeck128_256Key,
  expandKey as expandSpeck32_64Key,
  expandKey2 as expandSpeck48_72Key,
  expandKey3 as expandSpeck48_96Key,
  expandKey5 as expandSpeck64_128Key,
  expandKey4 as expandSpeck64_96Key,
  expandKey7 as expandSpeck96_144Key,
  expandKey6 as expandSpeck96_96Key,
  speck128_128,
  speck128_192,
  speck128_256,
  speck32_64,
  speck48_72,
  speck48_96,
  speck64_128,
  speck64_96,
  speck96_144,
  speck96_96
};
//# sourceMappingURL=index.mjs.map
